import KanbanIcon from '@atlaskit/icon-lab/core/kanban';
import ListChecklistIcon from "@atlaskit/icon/core/list-checklist";
import TableIcon from "@atlaskit/icon/core/table";
import {token} from "@atlaskit/tokens";
import type {ReactNode} from "react";
import {useMemo} from "react";
import {twJoin} from "tailwind-merge";
import {useIsMobile} from "~/utilities/ottokart/mobileQuotationView";
import SmartLinkListIcon from '@atlaskit/icon/core/smart-link-list';

export type QuotationsListViewMode = "table" | "kanban" | "list";

export type QuotationsListScope = "standard" | "advanced";

const MOBILE_BREAKPOINT_PX = 768;

const QUOTATIONS_LIST_VIEW_MODE_KEY = "quotations-list-view-mode:";
const QUOTATIONS_LIST_RETURN_INTENT_KEY = "quotations-list-return-intent:";

const VALID_VIEW_MODES = new Set<QuotationsListViewMode>([
    "table",
    "kanban",
    "list",
]);

export function parseQuotationsListViewMode(value: unknown): QuotationsListViewMode | undefined {
    if (typeof value === "string" && VALID_VIEW_MODES.has(value as QuotationsListViewMode)) {
        return value as QuotationsListViewMode;
    }
    return undefined;
}

export function rememberQuotationsListViewMode(mode: QuotationsListViewMode, scope: QuotationsListScope = "standard"): void {
    if (typeof sessionStorage === "undefined") {
        return;
    }
    sessionStorage.setItem(`${QUOTATIONS_LIST_VIEW_MODE_KEY}${scope}`, mode);
}

export function takeQuotationsListViewMode(scope: QuotationsListScope = "standard"): QuotationsListViewMode | null {
    if (typeof sessionStorage === "undefined") {
        return null;
    }
    const raw = sessionStorage.getItem(`${QUOTATIONS_LIST_VIEW_MODE_KEY}${scope}`);
    if (raw === null) {
        return null;
    }
    return parseQuotationsListViewMode(raw) ?? null;
}

/** Set when opening a quotation family from the list/kanban index so close can return there. */
export function rememberQuotationsListReturnIntent(scope: QuotationsListScope = "standard"): void {
    if (typeof sessionStorage === "undefined") {
        return;
    }
    sessionStorage.setItem(`${QUOTATIONS_LIST_RETURN_INTENT_KEY}${scope}`, "1");
}

export function hasQuotationsListReturnIntent(scope: QuotationsListScope = "standard"): boolean {
    if (typeof sessionStorage === "undefined") {
        return false;
    }
    return sessionStorage.getItem(`${QUOTATIONS_LIST_RETURN_INTENT_KEY}${scope}`) === "1";
}

export function clearQuotationsListReturnIntent(scope: QuotationsListScope = "standard"): void {
    if (typeof sessionStorage === "undefined") {
        return;
    }
    sessionStorage.removeItem(`${QUOTATIONS_LIST_RETURN_INTENT_KEY}${scope}`);
}

const DESKTOP_VIEW_ORDER = [
    "table",
    "kanban",
    "list",
] satisfies Array<QuotationsListViewMode>;
const MOBILE_VIEW_ORDER = [
    "list",
    "kanban",
    "table",
] satisfies Array<QuotationsListViewMode>;

const VIEW_MODE_CONFIG: Record<
    (typeof DESKTOP_VIEW_ORDER)[number],
    {
        label: string;
        icon: ReactNode;
    }
> = {
    table: {label: "Table", icon: <TableIcon label="" size="medium" />},
    kanban: {label: "Kanban", icon: <KanbanIcon label="" size="medium" />},
    list: {label: "List", icon: <SmartLinkListIcon label="" size="medium" />},
};

export function getDefaultQuotationsListViewMode(): QuotationsListViewMode {
    if (typeof window === "undefined") {
        return "table";
    }
    return window.innerWidth < MOBILE_BREAKPOINT_PX ? "list" : "table";
    return "table";
}

export function QuotationListViewSwitcher({
    value,
    onChange,
    className,
}: {
    value: QuotationsListViewMode;
    onChange: (mode: QuotationsListViewMode) => void;
    className?: string;
}) {
    const isMobile = useIsMobile(MOBILE_BREAKPOINT_PX);

    const orderedModes = useMemo(() => {
        const order = isMobile === true ? MOBILE_VIEW_ORDER : DESKTOP_VIEW_ORDER;
        return order.map((id) => ({id, ...VIEW_MODE_CONFIG[id]}));
    }, [isMobile]);

    return (
        <div
            className={twJoin("w-full border-b border-solid border-ds-border", className)}
            role="tablist"
            aria-label="Quotations view"
        >
            <div className="grid w-max max-w-full grid-flow-col auto-cols-max items-end justify-start gap-x-ds-space-50">
                {orderedModes.map((mode) => {
                    const isSelected = value === mode.id;
                    return (
                        <button
                            key={mode.id}
                            type="button"
                            role="tab"
                            aria-selected={isSelected}
                            aria-label={`${mode.label} view`}
                            onClick={() => onChange(mode.id)}
                            className={twJoin(
                                "grid grid-flow-col auto-cols-max items-center gap-x-ds-space-050 p-ds-space-100",
                                "-mb-px border-b-[3px] border-solid bg-transparent cursor-pointer",
                                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ds-border-accent-blue",
                                isSelected ? "text-ds-text-accent-blue" : "border-transparent text-ds-text hover:text-ds-text",
                            )}
                            style={{
                                borderBottomColor: isSelected ? token("color.border.accent.blue") : "transparent",
                            }}
                        >
                            <span
                                className="grid place-items-center shrink-0 text-current [&_svg]:text-current"
                                aria-hidden
                            >
                                {mode.icon}
                            </span>
                            <span className="text-sm font-semibold leading-none tracking-tight whitespace-nowrap">{mode.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
